import {
  Button,
  CloseRoundedIcon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Text,
} from '@shared/components';
import katex from 'katex';
import { useState } from 'react';

import { useRichTextEditor } from '../../providers';
import { MathFieldEditor } from '../math-field-editor';

export interface LatexEditorModalProps {
  /**
   * If true, the modal will be open
   */
  open: boolean;
  /**
   * The callback function when the modal is closed
   */
  onClose: () => void;
  /**
   * The initial value of the latex editor
   */
  initialValue?: string;
}

export default function LatexEditorModal(props: Readonly<LatexEditorModalProps>) {
  const { open: isOpen, onClose, initialValue } = props;
  const { editor } = useRichTextEditor();
  const [value, setValue] = useState(initialValue ?? '');
  const [error, setError] = useState<string | null>(null);

  if (!editor) {
    return null;
  }

  const handleClose = () => {
    setValue(initialValue ?? '');
    onClose();
    setError(null);
  };

  const handleInsertLatex = () => {
    try {
      setError(null);
      // NOTE: We need to throw an error if the math syntax is invalid
      katex.renderToString(value, { throwOnError: true });

      editor.chain().focus().insertLatex(value).run();
      setValue('');
      handleClose();
    } catch {
      setError('กรุณากรอกสัญลักษณ์ทางคณิตศาสตร์ให้ถูกต้อง');
    }
  };

  const handleChange = (value: string) => {
    setValue(value);
  };

  return (
    <Dialog id={`latex-editor-modal-${initialValue}`} open={isOpen} onClose={handleClose} fullWidth>
      <DialogTitle>Insert math equation</DialogTitle>
      <IconButton
        aria-label='close'
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
        }}
      >
        <CloseRoundedIcon />
      </IconButton>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => theme.spacing(2) }}
      >
        <MathFieldEditor onChange={handleChange} initialValue={value} />
        {error ? <Text sx={{ color: (theme) => theme.palette.error.main }}>{error}</Text> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleInsertLatex}>Insert</Button>
      </DialogActions>
    </Dialog>
  );
}
